/**
 * Reddit Pixel — lazy, runtime loader. Like `./facebookPixel` and `./googleAnalytics`, the
 * Pixel's `pixel.js` is injected on the FIRST call, never at module-eval time, so it loads only
 * after the owner has granted marketing consent. The pixel id comes from
 * `window.config.redditPixelId`.
 *
 * The initial `PageVisit` is NOT fired here — the app-wide route effect (`trackPageView`) owns
 * every page view so the first load isn't double-counted (mirrors the Pixel / GA loaders).
 *
 * Reddit's SDK has no runtime consent command (unlike `fbq('consent', …)`) and no disable flag
 * (unlike GA's `ga-disable-<id>`), and it never auto-tracks — it only sends what we explicitly
 * fire. So consent-off is honoured by an in-module `enabled` gate that every `rdt(…)` call here
 * checks: revoking consent stops all further Reddit collection, and re-granting resumes it
 * without a page reload.
 */

// Injected once per page, at module scope so it survives React remounts.
let injected = false;

// Marketing-consent gate. Reddit only fires what we tell it, so gating our own calls here fully
// stops collection after a revoke (no SDK-side consent/disable exists to lean on).
let enabled = false;

/* eslint-disable @typescript-eslint/no-explicit-any -- the rdt bootstrap is inherently dynamic */
/** The standard Reddit Pixel bootstrap (pixel.js). Idempotent via `window.rdt`. */
function bootstrapPixel(id: string): void {
  if (window.rdt) return;
  const p: any = (window.rdt = function (...args: any[]) {
    // `p.sendEvent(...args)` keeps `this === p`, exactly like Reddit's `.apply(p, …)` snippet.
    p.sendEvent ? p.sendEvent(...args) : p.callQueue.push(args);
  });
  p.callQueue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${encodeURIComponent(id)}`;
  const s = document.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(t, s);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Inject the Pixel and init `id` once. Idempotent.
 *
 * Advanced matching (email / phone / externalId) is intentionally NOT wired: this app never
 * hands PII to a third-party tracker (see `./sanitize`), so init runs without a match-key
 * object. Add one to the `rdt('init', …)` call here if that policy ever changes.
 */
export function loadRedditPixel(id: string): void {
  if (injected || !id) return;
  injected = true;
  bootstrapPixel(id);
  window.rdt?.('init', id);
}

/** Grant / revoke Reddit collection. Safe when the Pixel isn't loaded (the gate just flips). */
export function rdtSetConsent(granted: boolean): void {
  enabled = granted;
}

/** Record a page view. No-ops until rdt is present and consent is granted. */
export function rdtPageView(): void {
  if (!enabled) return;
  window.rdt?.('track', 'PageVisit');
}

/** Fire a standard Reddit event. No-ops without consent (or before the SDK loads). */
export function rdtTrack(event: string, params?: Record<string, unknown>): void {
  if (!enabled) return;
  params ? window.rdt?.('track', event, params) : window.rdt?.('track', event);
}

/** Fire a Reddit custom event (Reddit has no standard for it) via the `Custom` event type. */
export function rdtCustom(name: string, params?: Record<string, unknown>): void {
  rdtTrack('Custom', { customEventName: name, ...(params ?? {}) });
}
